import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { LogOut, RefreshCw, Loader2, Save, Package, AlertTriangle, Boxes } from 'lucide-react';
import { listInventory, upsertInventory, getToken, clearToken, me } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo, apparelProducts } from '../mock';

// Build the full grid: every product × every variant × every size
function buildGrid() {
  const rows = [];
  for (const p of apparelProducts) {
    const variants = p.variants.length ? p.variants : [''];
    const sizes = p.sizes.length ? p.sizes : [''];
    for (const v of variants) {
      for (const s of sizes) {
        rows.push({
          productId: p.id,
          productName: p.name,
          category: p.category,
          variant: v,
          size: s,
          key: `${p.id}::${(v || '').toLowerCase()}::${(s || '').toLowerCase()}`
        });
      }
    }
  }
  return rows;
}

const LOW_STOCK_THRESHOLD = 3;

export default function AdminInventory() {
  const { toast } = useToast();
  const [rows] = useState(() => buildGrid());
  const [stockByKey, setStockByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [authOk, setAuthOk] = useState(null);
  const [filterProduct, setFilterProduct] = useState('all');

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then(() => setAuthOk(true)).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const server = await listInventory();
      const map = {};
      for (const s of server) map[s.id] = s.stock;
      setStockByKey(map);
    } catch (e) {
      toast({ title: 'Error loading inventory', description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (authOk) load(); }, [authOk, load]);

  const setStock = (key, val) => {
    const num = Math.max(0, parseInt(val, 10) || 0);
    setStockByKey((prev) => ({ ...prev, [key]: num }));
  };

  const saveRow = async (row) => {
    setBusyKey(row.key);
    try {
      await upsertInventory({
        productId: row.productId,
        variant: row.variant,
        size: row.size,
        stock: stockByKey[row.key] ?? 0
      });
      toast({ title: 'Stock saved' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message });
    } finally { setBusyKey(null); }
  };

  const productOptions = useMemo(() => {
    const uniq = new Map();
    for (const r of rows) uniq.set(r.productId, r.productName);
    return Array.from(uniq.entries());
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (filterProduct === 'all') return rows;
    return rows.filter((r) => r.productId === filterProduct);
  }, [rows, filterProduct]);

  const totals = useMemo(() => {
    let totalUnits = 0;
    let outOfStock = 0;
    let lowStock = 0;
    for (const r of rows) {
      const s = stockByKey[r.key] ?? 0;
      totalUnits += s;
      if (s === 0) outOfStock += 1;
      else if (s <= LOW_STOCK_THRESHOLD) lowStock += 1;
    }
    return { totalUnits, outOfStock, lowStock, skus: rows.length };
  }, [rows, stockByKey]);

  if (authOk === null) return <div className="min-h-screen bg-[#0a1f44] flex items-center justify-center text-white">Loading…</div>;
  if (authOk === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#f5f6f8]">
      {/* Header */}
      <header className="bg-[#0a1f44] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto" />
            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Admin Console</div>
              <div className="font-display text-2xl leading-none mt-1">INVENTORY</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/scholars" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Scholars</Link>
            <Link to="/admin/orders" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Orders</Link>
            <Link to="/admin/account" data-testid="admin-nav-account" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Change Password</Link>
            <button
              onClick={() => { clearToken(); window.location.href = '/admin/login'; }}
              className="inline-flex items-center gap-1 text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20"
            >
              <LogOut className="w-3 h-3" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <section className="bg-white border-b border-[#0a1f44]/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center gap-6">
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50 flex items-center gap-1"><Boxes className="w-3 h-3" /> SKUs</div>
            <div className="font-display text-3xl text-[#0a1f44]" data-testid="inv-skus">{totals.skus}</div>
          </div>
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50 flex items-center gap-1"><Package className="w-3 h-3" /> Total Units</div>
            <div className="font-display text-3xl text-[#0a1f44]" data-testid="inv-total-units">{totals.totalUnits}</div>
          </div>
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Low Stock (≤{LOW_STOCK_THRESHOLD})</div>
            <div className="font-display text-3xl text-orange-600" data-testid="inv-low">{totals.lowStock}</div>
          </div>
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Out of Stock</div>
            <div className="font-display text-3xl text-[#c8102e]" data-testid="inv-out">{totals.outOfStock}</div>
          </div>
          <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-2">
            <select
              value={filterProduct}
              onChange={(e) => setFilterProduct(e.target.value)}
              data-testid="inv-product-filter"
              className="min-w-0 flex-1 sm:flex-none border border-[#0a1f44]/20 px-3 py-2 text-sm font-heading uppercase tracking-widest focus:outline-none focus:border-[#c8102e]"
            >
              <option value="all">All Products</option>
              {productOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 border border-[#0a1f44]/20 text-[#0a1f44] font-heading uppercase tracking-widest text-[10px] px-4 py-2 hover:bg-[#0a1f44] hover:text-white transition-colors"
              data-testid="inv-refresh"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="p-16 text-center text-[#0a1f44]/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="bg-white border border-[#0a1f44]/10 overflow-hidden">
            <table className="mobile-stack-table w-full text-sm" data-testid="inventory-table">
              <thead className="bg-[#0a1f44] text-white">
                <tr>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Product</th>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Category</th>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Color</th>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Size</th>
                  <th className="px-3 py-3 text-right font-heading uppercase tracking-widest text-[10px]">Stock</th>
                  <th className="px-3 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Status</th>
                  <th className="px-3 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Save</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const stock = stockByKey[r.key] ?? 0;
                  const isOut = stock === 0;
                  const isLow = !isOut && stock <= LOW_STOCK_THRESHOLD;
                  return (
                    <tr key={r.key} data-testid={`inv-row-${r.key}`} className="border-t border-[#0a1f44]/5 hover:bg-[#0a1f44]/[0.02]">
                      <td data-label="Product" className="px-3 py-2 font-heading text-[#0a1f44]">{r.productName}</td>
                      <td data-label="Category" className="px-3 py-2 text-xs text-[#0a1f44]/60 font-heading uppercase tracking-widest">{r.category}</td>
                      <td data-label="Color" className="px-3 py-2 text-[#0a1f44]/80">{r.variant || <span className="text-[#0a1f44]/40">—</span>}</td>
                      <td data-label="Size" className="px-3 py-2 text-[#0a1f44]/80">{r.size || <span className="text-[#0a1f44]/40">—</span>}</td>
                      <td data-label="Stock" className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          value={stock}
                          onChange={(e) => setStock(r.key, e.target.value)}
                          data-testid={`inv-stock-${r.key}`}
                          className="w-20 border border-[#0a1f44]/20 px-2 py-1 text-right font-display text-lg focus:outline-none focus:border-[#c8102e]"
                        />
                      </td>
                      <td data-label="Status" className="px-3 py-2 text-center">
                        {isOut ? (
                          <span className="inline-flex items-center gap-1 bg-[#c8102e]/10 text-[#c8102e] font-heading uppercase tracking-widest text-[9px] px-2 py-1">
                            Out
                          </span>
                        ) : isLow ? (
                          <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 font-heading uppercase tracking-widest text-[9px] px-2 py-1">
                            Low
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 font-heading uppercase tracking-widest text-[9px] px-2 py-1">
                            In Stock
                          </span>
                        )}
                      </td>
                      <td data-label="Save" className="px-3 py-2 text-center">
                        <button
                          onClick={() => saveRow(r)}
                          disabled={busyKey === r.key}
                          data-testid={`inv-save-${r.key}`}
                          className="inline-flex items-center gap-1 bg-[#0a1f44] hover:bg-[#c8102e] disabled:opacity-50 text-white font-heading uppercase tracking-widest text-[9px] px-3 py-1.5 transition-colors"
                        >
                          {busyKey === r.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                          Save
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-4 text-xs text-[#0a1f44]/50 font-heading uppercase tracking-widest">
          <Boxes className="w-3 h-3 inline mr-1" /> Inventory auto-decrements when customers place orders. Only admins can view or edit stock — the storefront never shows these numbers.
        </p>
      </section>
    </div>
  );
}
